import {
	Disclosure,
	DisclosureButton,
	DisclosurePanel,
	Menu,
	MenuButton,
	MenuItem,
	MenuItems,
	Transition,
} from '@headlessui/react';
import { Bars3Icon, UserIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Fragment, useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import Logo from '../components/Global/Logo';
import ALT_LOGO from '../assets/images/logos/logo-text.png';
import LOGO from '../assets/images/logos/logo-text.png';
import { useUser } from '../context/UserContext';
import Auth from '../utils/auth';
import seo from '../data';
import { PhoneIcon } from '@heroicons/react/20/solid';

function classNames(...classes) {
	return classes.filter(Boolean).join(' ');
}

export default function Navbar({ blackAlways = false }) {
	const { user, isLoggedIn } = useUser();
	const location = useLocation();
	const navigate = useNavigate();
	const [navigation, setNavigation] = useState([]);
	const [scrolled, setScrolled] = useState(false);
	const [scrollOrBlack, setScrollOrBlack] = useState(false);

	const contactLink = {
		name: 'Get a Free Estimate',
		href: '/#contact',
		current: location.hash === '#contact',
		onClick: (e) => {
			e.preventDefault();
			if (location.pathname === '/') {
				navigate('/#contact');
				document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
			} else {
				navigate('/#contact');
				setTimeout(() => {
					document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
				}, 500);
			}
		},
	};

	const callLink = {
		name: 'Give Us a Call',
		href: `tel:${seo.company.phone}`,
		current: false,
	};

	const homeLink = {
		name: 'Home',
		href: '/',
		current: location.pathname === '/' && !location.hash,
		onClick: () => {
			navigate('/');
			document.getElementById('root')?.scrollIntoView({ behavior: 'smooth' });
		},
	};
	const aboutLink = {
		name: 'About',
		href: '/about',
		current: location.pathname === '/' && location.hash === '#about',
		onClick: () => {
			navigate('/');
			document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' });
		},
	};

	// TODO: Add Phone Call Button for Mobile
	const setupNavigation = () => {
		if (user && isLoggedIn) {
			setNavigation([
				homeLink,
				{
					name: 'Services',
					href: '/services',
					current: location.pathname === '/services',
				},
				contactLink,
			]);
		} else {
			setNavigation([
				homeLink,
				aboutLink,
				{
					name: 'Services',
					href: '/services',
					current: location.pathname === '/services',
				},
				contactLink,
			]);
		}
		if (user && user.isAdmin)
			setNavigation((prev) => [
				...prev,
				{ name: 'Admin', href: '/admin', current: location.pathname === '/admin' },
			]);
	};

	useEffect(() => {
		setupNavigation();
	}, [location, user, isLoggedIn]);
	// Define navigation links

	// Scroll event listener
	useEffect(() => {
		const handleScroll = () => {
			setScrolled(window.scrollY > 50);
		};

		window.addEventListener('scroll', handleScroll);
		return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	useEffect(() => {
		setScrollOrBlack(scrolled || blackAlways);
	}, [scrolled, blackAlways]);
	return (
		<Disclosure
			as='nav'
			className={classNames(
				'z-50 fixed top-0 w-full transition-colors duration-300 h-18',
				scrollOrBlack ? 'bg-secondary text-white' : 'bg-secondary sm:bg-transparent text-black'
			)}
		>
			{({ open }) => (
				<>
					<div className='mx-auto max-w-7xl px-2 sm:px-6 lg:px-8'>
						<div className='relative flex h-16 items-center justify-between'>
							<div className='absolute inset-y-0 left-0 flex items-center sm:hidden '>
								{/* Mobile menu button */}
								<DisclosureButton className='inline-flex items-center justify-center rounded-md p-2 text-lg focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white'>
									<span className='sr-only'>Open main menu</span>
									{open ? (
										<XMarkIcon
											className='block h-10 w-10 text-white'
											aria-hidden='true'
										/>
									) : (
										<Bars3Icon
											className='block h-10 w-10 text-white'
											aria-hidden='true'
										/>
									)}
								</DisclosureButton>
							</div>
							<div className='flex flex-1 items-center justify-center sm:items-stretch sm:justify-start'>
								<div className='flex-shrink-0'>
									<Logo
										src={scrollOrBlack ? ALT_LOGO : LOGO}
										className='h-10 w-auto'
										alt='USA Blinds Logo'
									/>
								</div>

								{/* Desktop */}
								<div className='hidden sm:ml-6 sm:block'>
									<div className='flex space-x-4 align-baseline justify-normal'>
										{navigation.map((item) => (
											<Link
												key={item.name}
												to={item.href}
												onClick={item.onClick}
												className={classNames(
													item.current
														? 'text-white hover:text-gray-100 bg-primary'
														: 'text-gray-200 hover:text-gray-100',
													'rounded-md px-3 py-2 font-bold text-xl'
												)}
												aria-current={item.current ? 'page' : undefined}
											>
												{item.name}
											</Link>
										))}
									</div>
								</div>
							</div>

							{/* USER DROPDOWN */}
							<div className='absolute inset-y-0 right-0 flex items-center pr-2 sm:static sm:inset-auto sm:ml-6 sm:pr-0'>
								<a
									href={`tel:${seo.company.phone}`}
									className='hidden mr-5 sm:block font-bold px-4 py-3 rounded text-white font-xl hover:shadow-xl hover:bg-primary-600 hover:text-white transition ease-in duration-300'
								>
									<div
										className={
											(scrollOrBlack || location.pathname === '/' || location.pathname === ''
												? 'text-white'
												: 'text-black',
											'flex gap-x-3 align-baseline justify-normal items-center')
										}
									>
										<PhoneIcon
											className='h-5 w-5 inline-block'
											aria-hidden='true'
										/>
										<h3>{seo.company.phone}</h3>
									</div>
								</a>
								{/* Profile dropdown */}
								{user && isLoggedIn ? (
									<Menu
										as='div'
										className='relative'
									>
										<div>
											<MenuButton className='flex rounded-full bg-gray-60 text-lg focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800'>
												<img
													className='h-8 w-8 rounded-full'
													src={user.profilePicture || ''}
													alt=''
												/>
											</MenuButton>
										</div>
										<Transition
											as={Fragment}
											enter='transition ease-out duration-100'
											enterFrom='transform opacity-0 scale-95'
											enterTo='transform opacity-100 scale-100'
											leave='transition ease-in duration-75'
											leaveFrom='transform opacity-100 scale-100'
											leaveTo='transform opacity-0 scale-95'
										>
											<MenuItems className='absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none'>
												<MenuItem>
													<button
														onClick={Auth.logout}
														className='bg-gray-100 block px-4 py-2 text-sm text-gray-700 w-full text-left'
													>
														Sign out
													</button>
												</MenuItem>
											</MenuItems>
										</Transition>
									</Menu>
								) : (
									<div>
										<button
											type='button'
											onClick={() => navigate('/login')}
											className={classNames(
												'rounded-full p-1 hover:text-white focus:outline-none focus:ring-2',
												scrollOrBlack || location.pathname === '/' ? 'text-white' : 'text-black'
											)}
										>
											<span className='sr-only'>Login</span>
											<UserIcon
												className='h-6 w-6'
												aria-hidden='true'
											/>
										</button>
									</div>
								)}
							</div>
						</div>
					</div>

					{/* Mobile */}
					<DisclosurePanel className='sm:hidden bg-secondary'>
						<div className='space-y-4 px-2 pb-3 pt-2'>
							{navigation.map((item) => (
								<NavLink
									key={item.name}
									to={item.href}
									onClick={item.onClick}
									className={classNames(
										item.current
											? 'text-primary hover:text-white hover:bg-primary'
											: 'text-gray-50 hover:bg-gray-300 hover:text-black',
										'block rounded-md px-3 py-2 text-base font-medium w-full'
									)}
									aria-current={item.current ? 'page' : undefined}
								>
									{item.name}
								</NavLink>
							))}
						</div>
					</DisclosurePanel>
				</>
			)}
		</Disclosure>
	);
}
